import type {
  EligibilityCheck,
  GetCaseRecordEligibilityChecksRequest,
} from '@compass/client/types/eligibility';
import { eligibilityChecksService } from '@services/eligibilityChecks.service';
import { useQuery } from '@tanstack/react-query';

import { useStore } from '@/store';

// Sort eligibility checks by attempt in ascending order (attempt 1 first)
const sortEligibilityChecksByAttempt = (checks: EligibilityCheck[]): EligibilityCheck[] => {
  return [...checks].sort((a, b) => {
    // Handle missing attempt values - use 0 for missing attempts
    const aAttempt = a.attempt ?? 0;
    const bAttempt = b.attempt ?? 0;

    // Sort in ascending order (attempt 1 first)
    return aAttempt - bAttempt;
  });
};

export const useCaseRecordEligibilityChecks = (
  caseRecordId: string | null,
  filters?: Omit<
    GetCaseRecordEligibilityChecksRequest,
    'caserecord_id' | 'organization_id' | 'program_id'
  >,
  options?: { refetchInterval?: number | false },
) => {
  const testModeEnabled = useStore(state => state.testModeEnabled);
  const { selectedOrganizationId, selectedProgramId } = useStore();

  return useQuery({
    queryKey: ['caseRecordEligibilityChecks', caseRecordId, filters, testModeEnabled],
    queryFn: async () => {
      const response = await eligibilityChecksService.getCaseRecordEligibilityChecks({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        caserecord_id: caseRecordId!,
        ...filters,
      });

      // Return new object with sorted eligibility checks if they exist
      if (response.eligibility_checks && response.eligibility_checks.length > 0) {
        return {
          ...response,
          eligibility_checks: sortEligibilityChecksByAttempt(response.eligibility_checks),
        };
      }

      return response;
    },
    enabled: !!caseRecordId && !testModeEnabled, // Disable query in test mode
    staleTime: 5 * 60 * 1000, // 5 minutes - same as global default
    gcTime: 10 * 60 * 1000, // 10 minutes - same as global default
    refetchInterval: options?.refetchInterval,
  });
};

export const useEligibilityCheck = (
  caseRecordId: string | null,
  eligibilityCheckId: string | null,
  options?: { refetchInterval?: number | false },
) => {
  const testModeEnabled = useStore(state => state.testModeEnabled);
  const { selectedOrganizationId, selectedProgramId } = useStore();

  return useQuery({
    queryKey: ['eligibilityCheck', caseRecordId, eligibilityCheckId, testModeEnabled],
    queryFn: async () => {
      return eligibilityChecksService.getEligibilityCheck({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        caserecord_id: caseRecordId!,
        eligibility_check_id: eligibilityCheckId!,
      });
    },
    enabled: !!caseRecordId && !!eligibilityCheckId && !testModeEnabled, // Disable query in test mode
    staleTime: 5 * 60 * 1000, // 5 minutes - same as global default
    gcTime: 10 * 60 * 1000, // 10 minutes - same as global default
    refetchInterval: options?.refetchInterval,
  });
};
